/**
 * Imperative wrapper around the `companion_restart` Tauri command.
 *
 * Shared by the automatic reconnect hook ([[use-companion-reconnect]]) and
 * the manual reconnect ([[companion-reconnect-action]]). Resolves to `true` on a
 * successful stop+start cycle and `false` if either side failed (caller
 * decides how to surface the error — toast, fall back to retry, etc.).
 *
 * Single-flight: concurrent callers (auto loop, wrench, toast) share one
 * in-flight restart so native stop/start cycles never overlap.
 *
 * The Rust command waits for the previous run to fully tear down before
 * re-spawning, so callers do NOT need to sequence stop/start themselves.
 */
import { reportOperationalError } from "@/lib/operational-error";
import { invoke } from "@tauri-apps/api/core";
import { getAuthToken } from "./auth-token-store";
import { desktopApiOrigin } from "@/lib/release-channel";

const API_URL = desktopApiOrigin();

let inFlight: Promise<boolean> | null = null;

export function isCompanionRestartInFlight(): boolean {
  return inFlight !== null;
}

export function restartCompanion(): Promise<boolean> {
  inFlight ??= runRestart().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runRestart(): Promise<boolean> {
  const token = await getAuthToken().catch(() => null);
  if (!token) return false;
  try {
    await invoke("companion_restart", { token, apiUrl: API_URL });
    return true;
  } catch (error) {
    if ((await getAuthToken().catch(() => null)) === token) {
      reportOperationalError("companion_restart", error);
    }
    return false;
  }
}
